import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowRight, MapPin, Radio } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SegmentChips } from '@/components/common/SegmentChips';
import { segParam } from '@/lib/segments';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

export default function Campagnes() {
  const navigate = useNavigate();
  const { body, clearAll, parrainageOn, parrainageSeuil } = useFilters();
  const [seg, setSeg] = useState('ensemble');
  const segBody = parrainageOn ? { ...body, segment: segParam(seg), parrainage_seuil: parrainageSeuil } : body;
  const { data, isLoading } = usePost('/analytics/breakdown', { ...segBody, dim: 'campaign_code' });
  const items = data?.items || [];
  const maxBs = Math.max(1, ...items.map((i) => i.bs_reel));

  return (
    <div className="space-y-6">
      <PageHeader title="Campagnes" icon={Target}
        subtitle="Chaque code campagne dispose de son propre tableau de bord" />
      {parrainageOn && (
        <SegmentChips single value={seg} onChange={setSeg} testid="campagnes-segment" />
      )}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} height="h-28" />)}
        </div>
      ) : items.length === 0 ? <EmptyState onClear={clearAll} /> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((c) => (
            <button key={c.key} onClick={() => navigate(`/campagnes/${encodeURIComponent(c.key)}`)}
              className="group rounded-2xl border bg-card p-5 text-left card-shadow transition-all duration-200 hover:-translate-y-0.5 hover:card-shadow-lg"
              data-testid="campaign-card">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <span className="font-mono text-sm font-semibold accent-text">{c.key}</span>
                  {(c.canal || c.region) && (
                    <span className="mt-0.5 block break-words text-[11px] text-muted-foreground" data-testid="campaign-geo">
                      {[c.canal, c.region].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {c.association && (
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="h-2 w-2 rounded-full" style={{ background: c.association_color || '#EE722E' }} />
                      {c.association}
                    </span>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">BS validés</p>
                  <p className="font-display text-2xl font-semibold tabular">{fmtInt(c.bs_reel)}</p>
                </div>
                <Badge variant="secondary" className="rounded-full">{fmtPct(c.taux_transfo)} transfo</Badge>
              </div>
              <Progress value={(c.bs_reel / maxBs) * 100} className="mt-3 h-1.5" />
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-muted-foreground">Don moyen</p><p className="font-medium tabular">{fmtEur(c.don_moyen)}</p></div>
                <div><p className="text-muted-foreground">Âge moyen</p><p className="font-medium tabular">{fmtNum(c.age_moyen, 1)} ans</p></div>
                <div><p className="text-muted-foreground">Prod.</p><p className="font-medium tabular">{fmtNum(c.productivite, 2)}</p></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
